import React, { useEffect, useMemo } from 'react';
import { find } from 'lodash';
import { Form, Responsive } from 'connex-cds';
import { useConcreteClassesSetup, useMaterialComplianceSetup, useProductByRefSetup } from '../../../MasterDataProvider';
import { useTicketContext } from '../../../TicketContext';
import { MaterialComplianceMobile } from './MaterialComplianceMobile';
import { MaterialComplianceDesktop } from './MaterialComplianceDesktop';
import { findConcreteClassByRef, isValueOutOfRange } from './concreteClassRange';

const filterCompliance = type => compliance => compliance?.classType === type;

export const MaterialCompliance = () => {
  const materialComplianceSetup = useMaterialComplianceSetup();
  const { ticket } = useTicketContext();

  // Concrete-class ranges are only relevant when the primary line item is a concrete product.
  // Use the normalized systemProductType, which is reliably present on the line item's productType.
  const primaryLineItem = useMemo(() => find(ticket?.lineItems, { isPrimary: true }), [ticket?.lineItems]);
  const hasConcretePrimaryLineItem = useMemo(
    () => primaryLineItem?.productType?.systemProductType === 'CONCRETE',
    [primaryLineItem?.productType?.systemProductType]
  );

  const concreteClassesSetup = useConcreteClassesSetup({ enabled: hasConcretePrimaryLineItem });

  // The primary product carries the concrete-class refs (consistenceRef, airRef, temperatureRef, ...).
  const { data: product } = useProductByRefSetup(primaryLineItem?.item?.productRef, {
    enabled: hasConcretePrimaryLineItem,
  });

  const { values, setFieldValue } = Form.useFormContext();

  const loadingMaterialCompliance = useMemo(
    () => materialComplianceSetup?.isLoading,
    [materialComplianceSetup?.isLoading]
  );

  const activeMaterialComplianceSetup = useMemo(() => {
    if (materialComplianceSetup?.isSuccess && materialComplianceSetup?.data?.length) {
      return materialComplianceSetup?.data?.filter?.(compliance => compliance?.status === 'ACTIVE');
    }

    return [];
  }, [materialComplianceSetup?.data, materialComplianceSetup.isSuccess]);

  const air = useMemo(
    () =>
      [
        ...activeMaterialComplianceSetup?.filter?.(filterCompliance('air')),
        ...activeMaterialComplianceSetup?.filter?.(filterCompliance('AIR')),
      ]?.[0],
    [activeMaterialComplianceSetup]
  );

  const consistence = useMemo(
    () =>
      [
        ...activeMaterialComplianceSetup?.filter?.(filterCompliance('consistence')),
        ...activeMaterialComplianceSetup?.filter?.(filterCompliance('CONSISTENCE')),
      ]?.[0],
    [activeMaterialComplianceSetup]
  );

  const airTemp = useMemo(
    () =>
      [
        ...activeMaterialComplianceSetup?.filter?.(filterCompliance('airTemperature')),
        ...activeMaterialComplianceSetup?.filter?.(filterCompliance('AIR_TEMP')),
      ]?.[0],
    [activeMaterialComplianceSetup]
  );

  const concreteTemp = useMemo(
    () =>
      [
        ...activeMaterialComplianceSetup?.filter?.(filterCompliance('temperature')),
        ...activeMaterialComplianceSetup?.filter?.(filterCompliance('CONCRETE_TEMP')),
      ]?.[0],
    [activeMaterialComplianceSetup]
  );

  const cylinder = useMemo(
    () =>
      [
        ...activeMaterialComplianceSetup?.filter?.(filterCompliance('cylinders')),
        ...activeMaterialComplianceSetup?.filter?.(filterCompliance('CYLINDERS')),
      ]?.[0],
    [activeMaterialComplianceSetup]
  );

  const consistenceDisabled = useMemo(() => {
    return !values?.productCompliance?.consistenceValue?.value;
  }, [values?.productCompliance?.consistenceValue]);

  const airDisabled = useMemo(() => {
    return !values?.productCompliance?.airContentValue?.value;
  }, [values?.productCompliance?.airContentValue]);

  const airTempDisabled = useMemo(() => {
    return !values?.productCompliance?.airTempValue?.value;
  }, [values?.productCompliance?.airTempValue]);

  const concreteTempDisabled = useMemo(() => {
    return !values?.productCompliance?.concreteTempValue?.value;
  }, [values?.productCompliance?.concreteTempValue]);

  const cylinderNumberDisabled = useMemo(() => {
    return !values?.productCompliance?.cylinderNumber?.value;
  }, [values?.productCompliance?.cylinderNumber]);

  const materialComplianceNotFound = useMemo(
    () => [air, consistence, airTemp, concreteTemp, cylinder].every(material => !material),
    [air, consistence, airTemp, concreteTemp, cylinder]
  );

  const activeConcreteClasses = useMemo(() => {
    if (!hasConcretePrimaryLineItem || !concreteClassesSetup?.isSuccess) {
      return [];
    }

    return concreteClassesSetup?.data?.filter?.(concreteClass => concreteClass?.status === 'ACTIVE') ?? [];
  }, [concreteClassesSetup?.data, concreteClassesSetup?.isSuccess, hasConcretePrimaryLineItem]);

  // For each measurement field, the associated concrete class plus whether the entered value is out of range.
  // The concrete class is selected by the primary product: each measurement has its own ref
  // (consistenceRef, airRef, temperatureRef) that resolves to a concrete-class record by `crn`.
  // airTemp and cylinderNumber have no concrete-class counterpart, so they are intentionally left unranged.
  const ranges = useMemo(() => {
    const build = (concreteClassRef, value) => {
      const concreteClass = findConcreteClassByRef(activeConcreteClasses, concreteClassRef);
      return { concreteClass, outOfRange: isValueOutOfRange(value, concreteClass) };
    };

    const productCompliance = values?.productCompliance;

    return {
      consistence: build(product?.consistenceRef, productCompliance?.consistenceValue?.value),
      air: build(product?.airRef, productCompliance?.airContentValue?.value),
      concreteTemp: build(product?.temperatureRef, productCompliance?.concreteTempValue?.value),
    };
  }, [activeConcreteClasses, product, values?.productCompliance]);

  useEffect(() => {
    if (!consistenceDisabled || !airDisabled || !airTempDisabled || !concreteTempDisabled || !cylinderNumberDisabled) {
      setFieldValue('productCompliance.dateTest', new Date().toISOString());
    }
  }, [consistenceDisabled, airDisabled, airTempDisabled, concreteTempDisabled, cylinderNumberDisabled]);

  return (
    <Responsive>
      <MaterialComplianceMobile
        loadingMaterialCompliance={loadingMaterialCompliance}
        air={air}
        consistence={consistence}
        airTemp={airTemp}
        concreteTemp={concreteTemp}
        cylinder={cylinder}
        consistenceDisabled={consistenceDisabled}
        airDisabled={airDisabled}
        materialComplianceNotFound={materialComplianceNotFound}
        ranges={ranges}
      />
      <MaterialComplianceDesktop
        loadingMaterialCompliance={loadingMaterialCompliance}
        air={air}
        consistence={consistence}
        airTemp={airTemp}
        concreteTemp={concreteTemp}
        cylinder={cylinder}
        consistenceDisabled={consistenceDisabled}
        airDisabled={airDisabled}
        materialComplianceNotFound={materialComplianceNotFound}
        ranges={ranges}
      />
    </Responsive>
  );
};
